import { CircleAlert, Inbox, RefreshCw, X } from "lucide-react";
import type { ReactNode } from "react";
import { ApiError } from "../api/client.js";
import { consolePath } from "../app/brand.js";

const SKELETON_KEYS = [
  "alpha",
  "bravo",
  "charlie",
  "delta",
  "echo",
  "foxtrot",
  "golf",
  "hotel",
  "india",
  "juliet",
  "kilo",
  "lima",
] as const;

export function InlineError({
  error,
  onRetry,
  onDismiss,
}: {
  error: Error;
  onRetry?: () => void;
  onDismiss?: () => void;
}) {
  const sessionExpired = error instanceof ApiError && error.status === 401;
  return (
    <div className="inline-alert inline-alert--error" role="alert">
      <CircleAlert aria-hidden="true" size={16} />
      <span>
        {sessionExpired ? "Your review session has expired." : error.message}
        {error instanceof ApiError && error.requestId ? ` Request ${error.requestId}.` : ""}
      </span>
      {sessionExpired ? (
        <a
          className="button button--small"
          href={consolePath("/login")}
        >
          Sign in
        </a>
      ) : onDismiss ? (
        <button className="button button--small" type="button" onClick={onDismiss}>
          <X aria-hidden="true" size={14} />
          Dismiss
        </button>
      ) : onRetry ? (
        <button className="button button--small" type="button" onClick={onRetry}>
          <RefreshCw aria-hidden="true" size={14} />
          Retry
        </button>
      ) : null}
    </div>
  );
}

export function LoadingTable({ columns = 7, rows = 8 }: { columns?: number; rows?: number }) {
  return (
    <div className="loading-table" role="status" aria-label="Loading data">
      {SKELETON_KEYS.slice(0, rows).map((rowKey) => (
        <div className="loading-table__row" key={rowKey}>
          {SKELETON_KEYS.slice(0, columns).map((columnKey, columnIndex) => (
            <span
              className="loading-table__cell"
              key={`${rowKey}-${columnKey}`}
              style={{ opacity: Math.max(0.22, 0.72 - columnIndex * 0.07) }}
            />
          ))}
        </div>
      ))}
      <span className="sr-only">Loading data</span>
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <Inbox aria-hidden="true" size={24} />
      <strong>{title}</strong>
      <span>{description}</span>
      {action}
    </div>
  );
}
